/**
 * Create account.
 *
 * The password field shows a checklist, not a strength meter. This has been
 * rejected twice in the brief and the reason is worth keeping written down: a
 * coloured bar is a guess presented as a measurement, while these four lines are
 * the rules `AUTH_PASSWORD_VALIDATORS` actually enforces. Three of them the
 * browser can judge honestly. The fourth — Django's 20,000-word common-password
 * list — it cannot, so it stays `pending` and says so, rather than showing a tick
 * the server is about to contradict.
 */

import { useState, type FormEvent } from 'react';
import { Button } from '../design/Button';
import { TextField, PasswordField } from '../design/Field';
import { AuthLayout } from '../design/Feedback';
import { passwordRequirements } from '../api/auth';
import { FailureSummary, useSubmission } from './formSupport';
import { useTurnstile } from './Turnstile';
import { useAuth } from './AuthProvider';
import type { Route } from './routes';

export interface CreateAccountProps {
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}

export function CreateAccount({ navigate }: CreateAccountProps) {
  const { auth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { busy, failure, submit } = useSubmission();
  const turnstile = useTurnstile();

  const requirements = passwordRequirements(password, email);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const ok = await submit(async () => {
      const state = await auth.signUp(email.trim(), password, {
        turnstileToken: turnstile.token ?? undefined,
      });
      setPassword('');
      // With email verification set to `optional` the account is usable at once;
      // with `mandatory` the server answers verification-required. Both are
      // handled by asking the state rather than assuming either.
      if (state.status === 'verification-required') {
        navigate({ name: 'verify-email', key: null }, { replace: true });
      } else {
        navigate({ name: 'app', destination: 'home' }, { replace: true });
      }
    });
    if (!ok) {
      setPassword('');
      // A Turnstile token is single-use; keeping the spent one would make every
      // retry fail for a reason the learner cannot see.
      turnstile.reset();
    }
  }

  return (
    <AuthLayout
      heading="Create account"
      sub="An account keeps your work when you change iPad. You can keep writing without one."
      footer={
        <>
          Already have an account?{' '}
          <button
            type="button"
            className="mm-auth__link"
            onClick={() => navigate({ name: 'sign-in' })}
          >
            Sign in
          </button>
        </>
      }
    >
      <FailureSummary failure={failure} handledFields={['email', 'password']} />

      <form onSubmit={onSubmit} noValidate>
        <TextField
          label="Email"
          type="email"
          name="email"
          value={email}
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          required
          error={failure?.fields.email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <PasswordField
          label="Password"
          name="password"
          value={password}
          autoComplete="new-password"
          required
          requirements={requirements}
          error={failure?.fields.password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <turnstile.Widget />

        <div className="mm-auth__actions">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={busy}
            // Only blocks when a challenge is configured and unsolved. With no
            // site key there is no challenge and this is never true.
            disabled={turnstile.blocking}
          >
            Create account
          </Button>
        </div>
      </form>

      <div className="mm-auth__aside">
        <button type="button" className="mm-auth__link" onClick={() => navigate({ name: 'app', destination: 'home' })}>
          Continue without an account
        </button>
      </div>
    </AuthLayout>
  );
}
